import { MapContent, MapContentBase } from '../MapContent';
import { Map, MapMouseEvent, Popup } from 'maplibre-gl';
import { MapContentSource } from '../MapContentSource';
import { ShiftTrackLayer } from './ShiftTrackLayer';
import { ShiftTrackArrowsLayer } from './ShiftTrackArrowsLayer';
import { Feature, FeatureCollection, LineString, Point } from 'geojson';
import { TrackStyles } from '../../../../../configuration/model/TrackStyles';
import { ShiftMarkerSymbolLayer } from './ShiftMarkerSymbolLayer';
import { ShiftMarkerCircleLayer } from './ShiftMarkerCircleLayer';
import { ShiftMarkerShadowLayer } from './ShiftMarkerShadowLayer';
import { ShiftWithDriverAndVehicleModel } from '../../../../models/shift.model';
import { ShiftTrackPlowDownLayer } from './ShiftTrackPlowDownLayer';
import { ShiftTrackSpreadingLayer } from './ShiftTrackSpreadingLayer';
import { MapTools } from '../../../../tools/MapTools';
import { LineFeature, PointFeature, PointGeometry } from '../../../../models/GeoJson';
import { LocationSearchParams } from '../../../../models/breadcrumb.model';
import { ToastService } from '../../../../services/toast.service';
import {
  ApplicationRef,
  ComponentRef,
  Type,
  ViewContainerRef
} from '@angular/core';
import { ConfigurationModel } from '../../../../models/configuration.model';
import { ObservationShadowLayer } from './ObservationShadowLayer';
import { ObservationCircleLayer } from './ObservationCircleLayer';
import { ObservationIconLayer } from './ObservationIconLayer';
import { ObservationLabelLayer } from './ObservationLabelLayer';
import { ImageCircleLayer } from './ImageCircleLayer';
import { Observation } from '../../../../models/observation';
import {
  ObservationInfoWindowContentComponent
} from '../../../map-viewer/observation-info-window-content/observation-info-window-content.component';
import { ShiftMapDataService } from '../../../../../pages/shift-map/services/shift-map-data.service';
import { HighlightedItemSource } from '../../../../models/highlighted-item-update';
import { LocationApiService } from '../../../../../data/location-api/location-api.service';
import { ShiftTrackPaddingLayer } from './ShiftTrackPaddingLayer';
import { ImageCirclePaddingLayer } from './ImageCirclePaddingLayer';
import { RouteAssignment } from '../../../../models/route-assignment';
import moment from 'moment';
import { RouteLayer } from './RouteLayer';
import { RouteConfigurationWithSchema, RouteHierarchyItemWithPath } from '../../../../models/route';
import { Protocol } from 'pmtiles';

export class ShiftMapContent extends MapContentBase implements MapContent {

  private routesSource: MapContentSource = new MapContentSource('routes-source');
  private routeLayer: RouteLayer;

  private tracksSource: MapContentSource = new MapContentSource('shift-tracks-source');
  private shiftTrackLayer: ShiftTrackLayer;
  private shiftTrackPaddingLayer: ShiftTrackPaddingLayer;
  private shiftTrackPlowDownLayer: ShiftTrackPlowDownLayer;
  private shiftTrackSpreadingLayer: ShiftTrackSpreadingLayer;
  private shiftTrackArrowsLayer: ShiftTrackArrowsLayer;

  private readonly shiftTrackClickListener: any;
    private lastComponentRef: ComponentRef<any>;

  private shiftMarkersSource: MapContentSource = new MapContentSource('shift-markers-source');
  private shiftMarkerShadowLayer: ShiftMarkerShadowLayer;
  private shiftMarkerCircleLayer: ShiftMarkerCircleLayer;
  private shiftMarkerFlagLayer: ShiftMarkerSymbolLayer;

  private observationsSource: MapContentSource = new MapContentSource('shift-observations-source');
  private observationShadowLayer: ObservationShadowLayer;
  private observationCircleLayer: ObservationCircleLayer;
  private observationIconLayer: ObservationIconLayer;
  private observationLabelLayer: ObservationLabelLayer;

  private readonly observationClickListener: any;
  private lastPopup: Popup;

  private imagesSource: MapContentSource = new MapContentSource('shift-images-source');
  private imageCircleLayer: ImageCircleLayer;
  private imageCirclePaddingLayer: ImageCirclePaddingLayer;

  private readonly imageClickListener: any;

  // animation properties
  private shiftLineFeatures: Feature[];
  private shiftPointFeatures: Feature[];
  private shiftTimeOffset = 0;
  private shiftTimeLength = 0;

  private speedFactor = 10; // 1 = normal time, 10 = 10x faster
  private animation; // to store and cancel the animation
  private startTime = 0;
  private progress = 0; // progress = timestamp - startTime
  private resetTime = false; // indicator of whether time reset is needed for the animation
  private latestIndex = 0;
  private imageLastTime = 0;

  // binary search O(log n)
  static findLatestByThreshold<T>(
    arr: T[],
    threshold: number,
    getValue: (item: T) => number
  ): T | undefined {
    let left = 0;
    let right = arr.length - 1;
    let result: T | undefined = undefined;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const value = getValue(arr[mid]);

      if (value <= threshold) {
        result = arr[mid]; // Potential best match
        left = mid + 1;    // Search further in the right half
      } else {
        right = mid - 1;   // Search in the left half
      }
    }

    return result;
  }

  constructor(
    shiftTrackFeatureCollection: FeatureCollection,
    shiftMarkersFeatureCollection: FeatureCollection,
    observationFeatureCollection: FeatureCollection,
    imageFeatureCollection: FeatureCollection,
    routeFeatureCollection: FeatureCollection,
    private configuration: ConfigurationModel,
    trackStyles: TrackStyles,
    routeConfigurations: RouteConfigurationWithSchema[],
    routes: RouteHierarchyItemWithPath[],
    private observations: Observation[],
    private shift: ShiftWithDriverAndVehicleModel,
    private assignment: RouteAssignment,
    private locationApi: LocationApiService,
    private toast: ToastService,
    private viewContainerRef: ViewContainerRef,
    private appRef: ApplicationRef,
    private shiftMapDataManager: ShiftMapDataService,
    private vehicleMarkerClicked: (feature: PointFeature, unixTime: number) => void
  ) {
    super();

    this.routesSource.loadFromFeatureCollection(routeFeatureCollection);
    this.routeLayer = new RouteLayer(this.routesSource, routeConfigurations, routes, assignment);

    // shift track
    this.shiftLineFeatures = shiftTrackFeatureCollection.features;
    this.tracksSource.loadFromFeatureCollection(shiftTrackFeatureCollection);
    this.shiftTrackLayer = new ShiftTrackLayer(this.tracksSource, trackStyles, shift);
    this.shiftTrackPaddingLayer = new ShiftTrackPaddingLayer(this.tracksSource, trackStyles, shift);
    this.shiftTrackPlowDownLayer = new ShiftTrackPlowDownLayer(this.tracksSource, trackStyles, shift);
    this.shiftTrackSpreadingLayer = new ShiftTrackSpreadingLayer(this.tracksSource, trackStyles, shift);
    this.shiftTrackArrowsLayer = new ShiftTrackArrowsLayer(this.tracksSource, shift);

    this.shiftTrackClickListener = (e) => {
      if (MapTools.checkPropagation(e)) {
        return;
      }
      const lineFeature = e.features[0] as LineFeature;
      this.shiftTrackClicked(e, lineFeature);
    };

    // shift markers - start/end
    this.shiftMarkersSource.loadFromFeatureCollection(shiftMarkersFeatureCollection);
    this.shiftMarkerShadowLayer = new ShiftMarkerShadowLayer(this.shiftMarkersSource);
    this.shiftMarkerCircleLayer = new ShiftMarkerCircleLayer(this.shiftMarkersSource);
    this.shiftMarkerFlagLayer = new ShiftMarkerSymbolLayer(this.shiftMarkersSource);

    // shift observations
    this.observationsSource.loadFromFeatureCollection(observationFeatureCollection);
    this.observationShadowLayer = new ObservationShadowLayer(this.observationsSource);
    this.observationCircleLayer = new ObservationCircleLayer(this.observationsSource);
    this.observationIconLayer = new ObservationIconLayer(this.observationsSource);
    this.observationLabelLayer = new ObservationLabelLayer(this.observationsSource);

    this.observationClickListener = (e) => {
      if (MapTools.checkPropagation(e)) {
        return;
      }
      const pointFeature = e.features[0] as PointFeature;
      this.shiftMapDataManager.sendHighlightedObservationUpdate(
        pointFeature.properties.id,
        HighlightedItemSource.MAP,
      );
    };
    this.shiftMapDataManager.highlightedObservation$.subscribe(update => {
      if (!!update.itemId) {
        this.observationClicked(update.itemId, update.source);
      }
    });

    // shift images
    this.imagesSource.loadFromFeatureCollection(imageFeatureCollection);
    this.imageCircleLayer = new ImageCircleLayer(this.imagesSource, trackStyles);
    this.imageCirclePaddingLayer = new ImageCirclePaddingLayer(this.imagesSource, trackStyles);

    this.imageClickListener = (e) => {
      if (MapTools.checkPropagation(e)) {
        return;
      }
      const pointFeature = e.features[0] as PointFeature;
      this.shiftMapDataManager.sendHighlightedImageUpdate(
        pointFeature.properties.id,
        HighlightedItemSource.MAP,
      );
      e.originalEvent.stopPropagation();
    };
    this.shiftMapDataManager.highlightedImage$.subscribe(update => {
      if (!!update.itemId) {
        this.imageClicked(update.itemId, update.source);
      }
    });
  }

  load(map: Map, protocol: Protocol, zoomToFeatures: boolean) {
    this.map = map;
    this.firstSymbolLayerId = this.getFirstSymbolLayerId();

    // route layer
    this.routesSource.updateMap(this.map);
    this.map.addLayer(this.routeLayer.toLayerSpecification(), this.firstSymbolLayerId);

    // shift track
    this.tracksSource.updateMap(this.map);
    this.map.addLayer(this.shiftTrackLayer.toLayerSpecification(), this.firstSymbolLayerId);
    this.map.addLayer(this.shiftTrackPaddingLayer.toLayerSpecification(), this.firstSymbolLayerId);
    this.map.addLayer(this.shiftTrackArrowsLayer.toLayerSpecification(), this.firstSymbolLayerId);
    this.map.addLayer(this.shiftTrackPlowDownLayer.toLayerSpecification(), this.firstSymbolLayerId);
    this.map.addLayer(this.shiftTrackSpreadingLayer.toLayerSpecification(), this.firstSymbolLayerId);

    // shift images
    this.imagesSource.updateMap(this.map);
    this.map.addLayer(this.imageCircleLayer.toLayerSpecification());
    this.map.addLayer(this.imageCirclePaddingLayer.toLayerSpecification());
    MapTools.pointCursorForLayer(this.map, this.imageCirclePaddingLayer, true);
    this.map.on('click', this.imageCirclePaddingLayer.layerId, this.imageClickListener);

    MapTools.pointCursorForLayer(this.map, this.shiftTrackPaddingLayer, true);
    this.map.on('click', this.shiftTrackPaddingLayer.layerId, this.shiftTrackClickListener);

    // shift observations
    this.observationsSource.updateMap(this.map);
    this.map.addLayer(this.observationShadowLayer.toLayerSpecification());
    this.map.addLayer(this.observationCircleLayer.toLayerSpecification());
    this.map.addLayer(this.observationIconLayer.toLayerSpecification());
    this.map.addLayer(this.observationLabelLayer.toLayerSpecification());
    MapTools.pointCursorForLayer(this.map, this.observationCircleLayer, true);
    this.map.on('click', this.observationCircleLayer.layerId, this.observationClickListener);

    // shift markers - start/end/vehicle
    this.shiftMarkersSource.updateMap(this.map);
    this.map.addLayer(this.shiftMarkerShadowLayer.toLayerSpecification());
    this.map.addLayer(this.shiftMarkerCircleLayer.toLayerSpecification());
    this.map.addLayer(this.shiftMarkerFlagLayer.toLayerSpecification());

    if (zoomToFeatures) {
      this.shiftTrackLayer.zoomToFeatures(this.map);
    }
  }

  unload() {
    MapTools.pointCursorForLayer(this.map, this.imageCirclePaddingLayer, false);
    this.map.off('click', this.imageCirclePaddingLayer.layerId, this.imageClickListener);
    this.map.removeLayer(this.imageCircleLayer.layerId);
    this.map.removeLayer(this.imageCirclePaddingLayer.layerId);
    this.map.removeSource(this.imagesSource.sourceId);

    MapTools.pointCursorForLayer(this.map, this.observationCircleLayer, false);
    this.map.off('click', this.observationCircleLayer.layerId, this.observationClickListener);
    this.map.removeLayer(this.observationLabelLayer.layerId);
    this.map.removeLayer(this.observationIconLayer.layerId);
    this.map.removeLayer(this.observationCircleLayer.layerId);
    this.map.removeLayer(this.observationShadowLayer.layerId);
    this.map.removeSource(this.observationsSource.sourceId);

    MapTools.pointCursorForLayer(this.map, this.shiftTrackPaddingLayer, false);
    this.map.off('click', this.shiftTrackPaddingLayer.layerId, this.shiftTrackClickListener);
    this.map.removeLayer(this.shiftTrackLayer.layerId);
    this.map.removeLayer(this.shiftTrackPaddingLayer.layerId);
    this.map.removeLayer(this.shiftTrackPlowDownLayer.layerId);
    this.map.removeLayer(this.shiftTrackSpreadingLayer.layerId);
    this.map.removeLayer(this.shiftTrackArrowsLayer.layerId);
    this.map.removeSource(this.tracksSource.sourceId);

    this.map.removeLayer(this.shiftMarkerFlagLayer.layerId);
    this.map.removeLayer(this.shiftMarkerCircleLayer.layerId);
    this.map.removeLayer(this.shiftMarkerShadowLayer.layerId);
    this.map.removeSource(this.shiftMarkersSource.sourceId);

    this.map.removeLayer(this.routeLayer.layerId);
    this.map.removeSource(this.routesSource.sourceId);
    this.map = null;
  }

  // external trigger to move vehicle marker
  public changeVehiclePosition(unixtime: number) {
    if (this.map) {
      const searchParams = {
        timeFrom: unixtime - 5,
        timeTo: unixtime + 5,
        radius: 110,
        locationSources: [this.shift.vehicleId],
        sessions: [this.shift.id],
      } as LocationSearchParams;
      this.searchBreadcrumb(unixtime, searchParams, (features) => {
        let feature: PointFeature;
        features.forEach(f => {
          if (!feature || Math.abs(f.properties.unixtime - unixtime) < Math.abs(feature.properties.unixtime - unixtime)) {
            feature = f;
          }
        });
        this.vehicleMarkerClicked(feature, feature.properties.unixtime);
        this.moveVehicleMarker(
          feature.geometry.coordinates[0],
          feature.geometry.coordinates[1],
          feature.properties.heading,
        );
        this.updateImageBasedOnTime(feature.properties.unixtime);
      });
    } else {
      console.warn('Map not initialized');
    }
  }

  private shiftTrackClicked(event: MapMouseEvent, lineFeature: LineFeature) {
    if (!!this.map) {
      const searchParams = {
        coordinate: {
          latitude: event.lngLat.lat,
          longitude: event.lngLat.lng
        },
        timeFrom: lineFeature.properties.time_start,
        timeTo: lineFeature.properties.time_end,
        radius: 110,
        locationSources: [lineFeature.properties.locationsourceid],
        sessions: [lineFeature.properties.sessionkey],
      } as LocationSearchParams;
      this.searchBreadcrumb(null, searchParams, (features) => {
        const feature = features[0];
        this.vehicleMarkerClicked(feature, feature.properties.unixtime);
        this.moveVehicleMarker(
          feature.geometry.coordinates[0],
          feature.geometry.coordinates[1],
          feature.properties.heading,
        );
        this.updateImageBasedOnTime(feature.properties.unixtime);
      });
    } else {
      console.warn('Map not initialized');
    }
  }

  private observationClicked(observationId: number, source: HighlightedItemSource) {
    this.observationsSource.features.forEach(feature => {
      feature.properties.highlighted = feature.properties.id === observationId;
    });
    if (!!this.map) {
      this.observationsSource.updateMap(this.map);
      const observation = this.observations.find(o => o.id === observationId);
      if (!!observation) {
        this.showObservationInfo(observation);
      }
    } else {
      console.warn('Map not initialized');
    }
  }

  private imageClicked(imageId: number, source: HighlightedItemSource) {
    let pointFeature: PointFeature;
    this.imagesSource.features.forEach(feature => {
      feature.properties.highlighted = feature.properties.id === imageId;
      if (feature.properties.id === imageId) {
        pointFeature = feature as PointFeature;
      }
    });
    if (!!this.map) {
      this.imagesSource.updateMap(this.map);
      if (source === HighlightedItemSource.PANEL) {
        MapTools.zoomToPointFeature(this.map, pointFeature, undefined);
      }
    } else {
      console.warn('Map not initialized');
    }
  }

  // parameters: percentage
  filterByTime(timeFrom: number, timeTo: number) {
    MapTools.onMap(this.map, () => {
      this.shiftTrackLayer.filterByTime(this.map, timeFrom, timeTo);
      this.shiftTrackPaddingLayer.filterByTime(this.map, timeFrom, timeTo);
      this.shiftTrackPlowDownLayer.filterByTime(this.map, timeFrom, timeTo);
      this.shiftTrackSpreadingLayer.filterByTime(this.map, timeFrom, timeTo);
      this.shiftTrackArrowsLayer.filterByTime(this.map, timeFrom, timeTo);
    });
  }

  changeAnimationSpeed(speedFactor: number) {
    this.speedFactor = speedFactor;
  }

  loadDataIfNeededAndPlay(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!this.shiftPointFeatures) {
        this.locationApi.getShiftPoints(
          this.shift.id,
          this.shift.vehicleId,
          this.assignment?.onAssignmentFrom,
          this.assignment?.completed,
        ).then(featureCollection => {
          const points = featureCollection.features;
          this.shiftPointFeatures = points;
          if (points.length > 0) {
            // adjust the geometry - ensure it contains starting point at the shift start timestamp
            const shiftStartMoment = moment(this.assignment ? this.assignment.onAssignmentFrom : this.shift.start);
            if (shiftStartMoment.unix() !== points[0].properties['unixtime']) {
              const startFeature = points[0];
              startFeature.properties['unixtime'] = shiftStartMoment.unix();
              this.shiftPointFeatures.splice(0, 0, startFeature);
            }
            // adjust the geometry - ensure it contains point at the shift end timestamp
            const shiftEndMoment = moment(this.assignment ? this.assignment.completed : this.shift.end);
            if (shiftEndMoment.unix() > points[points.length - 1].properties['unixtime']) {
              const endFeature = points[points.length - 1];
              endFeature.properties['unixtime'] = shiftEndMoment.unix();
              this.shiftPointFeatures.push(endFeature);
            }
            this.shiftTimeOffset = points[0].properties['unixtime'];
            this.shiftTimeLength = points[points.length - 1].properties['unixtime'] - this.shiftTimeOffset;
          }
          this.playAnimation();
          resolve(true);
        }).catch(error => reject(error));
      } else {
        // using existing points
        this.playAnimation();
        resolve(true);
      }
    });
  }

  getShiftTimeOffset(): number {
    if (!!this.shift) {
      return this.shiftTimeOffset;
    } else {
      console.warn('Incorrect usage! Shift Playback service not initialized!');
      return 0;
    }
  }

  playAnimation() {
    // eslint-disable-next-line no-console
    console.log('playing animation');
    this.startTime = performance.now();
    this.resetTime = true;
    this.imageLastTime = 0;
    this.animateLine(this.startTime);
  }

  // DOMHighResTimeStamp parameter
  private animateLine(timestamp) {
    if (this.resetTime) {
      // resume previous progress
      this.startTime = timestamp - this.progress;
      this.resetTime = false;
    } else {
      this.progress = timestamp - this.startTime;
    }

    if (this.progress / 1000 * this.speedFactor > this.shiftTimeLength) {
      // eslint-disable-next-line no-console
      console.log('Shift end has been reached.');
      this.shiftMapDataManager.sendShiftEndReached();
      return;
    }

    const calcNextPointTime = this.progress / 1000 * this.speedFactor;
    const nextPointTime = this.shiftPointFeatures[this.latestIndex].properties['unixtime'] - this.shiftTimeOffset;
    if (nextPointTime < calcNextPointTime) {
      // use the latest point and move index
      this.vehicleMarkerClicked(
        this.shiftPointFeatures[this.latestIndex] as PointFeature,
        this.shiftPointFeatures[this.latestIndex].properties['unixtime'],
      );
      this.moveVehicleMarker(
        (this.shiftPointFeatures[this.latestIndex].geometry as PointGeometry).coordinates[0],
        (this.shiftPointFeatures[this.latestIndex].geometry as PointGeometry).coordinates[1],
        this.shiftPointFeatures[this.latestIndex].properties['heading'],
      );
      this.latestIndex = this.latestIndex + 1;
    } else {
      // keeping index and interpolate
      if (this.latestIndex > 0) {
        const from = this.shiftPointFeatures[this.latestIndex - 1];
        const fromLng = (from.geometry as PointGeometry).coordinates[0];
        const fromLat = (from.geometry as PointGeometry).coordinates[1];
        const fromTime = from.properties['unixtime'] - this.shiftTimeOffset;
        const to = this.shiftPointFeatures[this.latestIndex];
        const toLng = (to.geometry as PointGeometry).coordinates[0];
        const toLat = (to.geometry as PointGeometry).coordinates[1];
        const toTime = to.properties['unixtime'] - this.shiftTimeOffset;
        const timeDiff = toTime - fromTime;
        const timeBetweenPoints = calcNextPointTime - fromTime;
        const pctBetweenPoints = timeBetweenPoints / timeDiff;
        const interpolatedLng = fromLng + (toLng - fromLng) * pctBetweenPoints;
        const interpolatedLat = fromLat + (toLat - fromLat) * pctBetweenPoints;
        this.vehicleMarkerClicked(this.shiftPointFeatures[this.latestIndex - 1] as PointFeature, calcNextPointTime + this.shiftTimeOffset);
        this.moveVehicleMarker(
          interpolatedLng,
          interpolatedLat,
          to.properties['heading'],
        );
      }
    }

    // show the latest image based on animation position, refresh every second
    const imageAnimationRefreshInterval = 1000;
    if (this.imagesSource.features.length > 0 && timestamp - this.imageLastTime > imageAnimationRefreshInterval) {
      this.imageLastTime = timestamp;
      this.updateImageBasedOnTime(this.shiftPointFeatures[this.latestIndex].properties.unixtime);
    }

    // Request the next frame of the animation.
    this.animation = requestAnimationFrame(time => this.animateLine(time));
  }

  pauseAnimation() {
    // eslint-disable-next-line no-console
    console.log('pausing animation...');
    cancelAnimationFrame(this.animation);
  }

  stopAnimation() {
    // eslint-disable-next-line no-console
    console.log('stopping animation...');
    this.latestIndex = 0;
    this.progress = 0;
    cancelAnimationFrame(this.animation);
  }

  jumpToPosition(newProgress: number) {
    // eslint-disable-next-line no-console
    console.log('jumping to position: ' + newProgress);
    this.progress = newProgress / this.speedFactor * 1000;
    let index = 0;
    for (const pointFeature of this.shiftPointFeatures) {
      const pointTime = pointFeature.properties['unixtime'] - this.shiftTimeOffset;
      if (pointTime > newProgress) {
        this.latestIndex = index;
        this.moveVehicleMarker(
          (pointFeature.geometry as PointGeometry).coordinates[0],
          (pointFeature.geometry as PointGeometry).coordinates[1],
        );
        break;
      }
      index += 1;
    }

    if (this.imagesSource.features.length > 0) {
      this.updateImageBasedOnTime(this.shiftPointFeatures[this.latestIndex].properties.unixtime);
    }
  }

  private updateImageBasedOnTime(unixtime: number) {
    const imageFeature = ShiftMapContent.findLatestByThreshold(
      this.imagesSource.features,
      unixtime,
      item => item.properties.time,
    );

    if (!!imageFeature) {
      this.shiftMapDataManager.sendHighlightedImageUpdate(
        imageFeature.properties.id,
        HighlightedItemSource.MAP,
      );
    }
  }

  private searchBreadcrumb(
    unixtime: number,
    searchParams: LocationSearchParams,
    handleFoundBreadcrumbs: (features: PointFeature[]) => void,
  ) {
    // use points from animation source if available
    if (this.shiftPointFeatures) {
      const features = this.shiftPointFeatures.filter(feature => {
        return feature.properties['unixtime'] >= searchParams.timeFrom && feature.properties['unixtime'] <= searchParams.timeTo;
      });
      if (features.length === 0) {
        this.fallbackSearch(unixtime, searchParams, handleFoundBreadcrumbs);
      } else {
        // console.log('Using cached points...');
        let closestFeature: PointFeature;
        let closestDistance = 1000;
        for (const feature of features) {
          let distance: number;
          if (unixtime) {
            distance = Math.abs(unixtime - feature.properties['unixtime']);
          } else {
            distance = this.calculateEuclideanDistance(
              {lng: searchParams.coordinate.longitude, lat: searchParams.coordinate.latitude},
              {lng: (feature.geometry as Point).coordinates[0], lat: (feature.geometry as Point).coordinates[1]},
            );
          }
          if (distance < closestDistance) {
            closestFeature = feature as PointFeature;
            closestDistance = distance;
          }
        }
        handleFoundBreadcrumbs([closestFeature]);
      }
    } else {
      this.locationApi.search(searchParams).then(response => {
        const features = response.features;
        if (features.length === 0) {
          this.fallbackSearch(unixtime, searchParams, handleFoundBreadcrumbs);
        } else {
          handleFoundBreadcrumbs(features as PointFeature[]);
        }
      }).catch(error => {
        console.error('Breadcrumbs search failed.', error);
        this.toast.short('Breadcrumbs search failed.');
      });
    }
  }

  // when no breadcrumb is found get the feature from LineString
  private fallbackSearch(unixtime: number, searchParams: LocationSearchParams, handleFoundBreadcrumbs: (features: PointFeature[]) => void) {
    let lineFeature: Feature;
    let getFirst = true;
    // find the first suitable lineString ... there might be gaps, even at the shift start or shift end
    for (let i = 0; i < this.shiftLineFeatures.length ; i++) {
      const l = this.shiftLineFeatures[i];
      // use the first as default
      if (!lineFeature) {
        lineFeature = l;
      }
      if (unixtime) {
        if (unixtime >= lineFeature.properties['time_start']) {
          lineFeature = l;
        } else {
          getFirst = i === 0;
          break;
        }
        if (unixtime < lineFeature.properties['time_end']) {
          break;
        } else if (i === this.shiftLineFeatures.length - 1) {
          // if it was the last lineString
          getFirst = false;
        }
      } else {
        if (searchParams.timeFrom >= lineFeature.properties['time_start'] &&
          searchParams.timeTo <= lineFeature.properties['time_end']) {
          lineFeature = l;
        }
      }
    }
    if (lineFeature) {
      const lineStringCoordinates = (lineFeature.geometry as LineString).coordinates;
      const pointFeature = JSON.parse(JSON.stringify(lineFeature));
      (pointFeature.geometry as Point).coordinates = lineStringCoordinates[getFirst ? 0 : lineStringCoordinates.length - 1];
      pointFeature.geometry.type = 'Point';
      pointFeature.properties['time_end'] = undefined;
      pointFeature.properties['time_start'] = undefined;
      pointFeature.properties['speed'] = 0;
      pointFeature.properties['unixtime'] = getFirst ? lineFeature.properties['time_start'] : lineFeature.properties['time_end'];
      // eslint-disable-next-line no-console
      console.log('Using fallback search...');
      handleFoundBreadcrumbs([pointFeature]);
    } else {
      console.warn('No breadcrumbs found even on fallback search.');
    }
  }

  // Simple Euclidean distance (for projected coordinates or small areas)
  private calculateEuclideanDistance(
    point1: { lat: number; lng: number },
    point2: { lat: number; lng: number }
  ): number {
    const dx = point2.lng - point1.lng;
    const dy = point2.lat - point1.lat;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private instantiateComponent<T>(componentType: Type<T>): ComponentRef<T> {
    const componentRef = this.viewContainerRef.createComponent(componentType);
    componentRef.onDestroy(() => {
      this.appRef.detachView(componentRef.hostView);
    });
    this.lastComponentRef = componentRef;

    return componentRef;
  }

  private showObservationInfo(observation: Observation) {
    this.lastPopup?.remove();
    const infoComponentRef = this.instantiateComponent(ObservationInfoWindowContentComponent);
    infoComponentRef.setInput('observation', observation);
    infoComponentRef.setInput('isLiveMap', false);

    const div = document.createElement('div');
    div.appendChild(this.lastComponentRef.location.nativeElement);

    this.lastPopup = new Popup({offset: [0, -15], className: 'marker-popup', maxWidth: '300px'})
      .setLngLat([observation.location.longitude, observation.location.latitude])
      .setDOMContent(div)
      .addTo(this.map);
  }

  private moveVehicleMarker(longitude: number, latitude: number, bearing = 0) {
    this.shiftMarkersSource.features = this.shiftMarkersSource.features.map(feature => {
      if (feature.properties['vehicle']) {
        feature.properties['heading'] = bearing;
        (feature.geometry as Point).coordinates = [longitude, latitude];
      }
      return feature;
    });
    this.shiftMarkersSource.updateMap(this.map);
  }
}
